import { move } from '@dnd-kit/helpers'
import type { FormField, FormSection, SelectOption } from '@/components/form-builder/types'
import { defaultCollisionDetection, directionBiased } from '@dnd-kit/collision'

/**
 * Pure drag-and-drop placement helpers for the Form Builder canvas.
 *
 * Extracted from FormEditorPage so the move logic can be unit-tested without a
 * DOM: the repo's vitest run is node-only and matches `src/**\/*.test.ts`.
 * Pointer and keyboard drags both funnel through dnd-kit's single drag-operation
 * pipeline, so both sensors exercise `applyFieldMove` — there is no separate
 * keyboard code path to cover.
 */

/**
 * dnd-kit's dragover/dragend event. Typed loosely on purpose: `@dnd-kit/helpers`'
 * `move` is generic over four manager type parameters that aren't worth
 * threading through here, and the page's handlers receive the event as `any`.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type FieldDragEvent = any

/**
 * Sortable-group / droppable id for a section's field container. Every field in
 * a section shares this as its `useSortable` group, and the container itself
 * registers it as a droppable id — so it doubles as the key `applyFieldMove`
 * uses to hand dnd-kit a per-section view of the field lists. Import it rather
 * than re-deriving the prefix; the two must not drift.
 */
export const sectionGroupId = (sectionId: string) => `sec:${sectionId}`

/** dnd-kit `type` for a section's own drag (z8nrz7e1wt). */
export const SECTION_DRAG_TYPE = 'section'

/**
 * `accept` for everything INSIDE a section — its field list and its fields: any
 * drag but a section's own (z8nrz7e1wt).
 *
 * Sections only ever reorder among sections. Before this, a section dragged by
 * its grip over another section's field could win the collision, and `useSortable`'s
 * OptimisticSortingPlugin — which re-parents the dragged DOM node on dragover
 * whenever source and target are both sortables — moved the whole section
 * element INTO that section's field list, behind React's back. The page never
 * nests sections, so the drop just reordered the flat list, and React left the
 * node where the plugin put it: on screen, a "section inside a section". The
 * next change that made React remove or move that node ("delete the inner
 * section") failed with NotFoundError: removeChild, because React asked the
 * canvas to drop a child that now lived inside another section. The same
 * mechanism as the cross-section field crash (86eyfxwky, see handleDragOver),
 * reached by a section drag, which nothing constrained.
 */
export const acceptsAllButSections = (source: { type?: unknown }) => source.type !== SECTION_DRAG_TYPE

/**
 * Collision detection for a section card (z8nrz7e1wt).
 *
 * A section's drag uses `directionBiased`: dragging up, only sections whose top
 * is above the dragged section's centre are candidates, and dragging down only
 * those whose bottom is below it. The default (pointer, then shape, closest
 * centre wins) flip-flopped between a tall section and the shorter one being
 * dragged over it, so the drop landed wherever the flip-flop stopped — that
 * only ever looked right while the tall section's FIELDS caught the drag, which
 * is the re-parenting this ticket removes. Every other drag over a section card
 * (a field dropped on its header) keeps the default.
 */
export const sectionCollisionDetector: typeof defaultCollisionDetection = input =>
  input.dragOperation.source?.type === SECTION_DRAG_TYPE ? directionBiased(input) : defaultCollisionDetection(input)

/** Resolve which section a drop landed in. targetId is a section sortable id
 *  (`section:<id>`), a section field-container droppable id (`sec:<id>`), or an
 *  existing field id (→ that field's section). */
export function resolveSectionId(secs: FormSection[], targetId: string | null): string | null {
  if (!targetId) return null
  if (targetId.startsWith('sec:')) return targetId.slice(4)
  if (targetId.startsWith('section:')) return targetId.slice(8)
  return secs.find(s => s.fields.some(f => f.id === targetId))?.id ?? null
}

/**
 * The section list after a section drag ends (z8nrz7e1wt).
 *
 * Sections reorder in one container, where dnd-kit's optimistic sorting has
 * already slid the dragged section to its new slot by the time it is dropped —
 * so the pointer usually ends over the dragged section ITSELF, and the drop
 * target names no other section. The slot is `index` on the sortable source;
 * when it moved, that is where the section goes, so state matches what the drag
 * showed. Otherwise the drop target decides, as before: the section is placed
 * before the section it resolves to. Unchanged, the same array comes back.
 */
export function reorderSections(
  sections: FormSection[],
  sectionId: string,
  drop: { index?: number; initialIndex?: number; targetId: string | null },
): FormSection[] {
  const fromIdx = sections.findIndex(s => s.id === sectionId)
  if (fromIdx === -1) return sections
  const moved = sections[fromIdx]
  const without = sections.filter(s => s.id !== sectionId)
  if (drop.index !== undefined && drop.initialIndex !== undefined && drop.index !== drop.initialIndex) {
    const to = Math.max(0, Math.min(drop.index, without.length))
    if (to === fromIdx) return sections
    return [...without.slice(0, to), moved, ...without.slice(to)]
  }
  const destId = resolveSectionId(sections, drop.targetId)
  if (!destId || destId === sectionId) return sections
  const destIdx = without.findIndex(s => s.id === destId)
  return destIdx === -1 ? [...without, moved] : [...without.slice(0, destIdx), moved, ...without.slice(destIdx)]
}

export const isFieldTarget = (targetId: string | null): targetId is string =>
  !!targetId && !targetId.startsWith('sec:') && !targetId.startsWith('section:')

/**
 * Apply an in-flight field drag to the section tree, delegating the actual
 * index math to dnd-kit's `move` so the committed layout matches exactly what
 * the drag preview showed.
 *
 * Returns the input array unchanged when the drag doesn't resolve to a move —
 * `move` calls `preventDefault()` in that case, which is also what tells
 * dnd-kit's OptimisticSortingPlugin to keep its hands off the DOM.
 */
export function applyFieldMove(sections: FormSection[], event: FieldDragEvent): FormSection[] {
  const groups: Record<string, FormField[]> = {}
  for (const s of sections) groups[sectionGroupId(s.id)] = s.fields
  const next = move(groups, event)
  if (next === groups) return sections
  return sections.map(s => {
    const fields = next[sectionGroupId(s.id)]
    return !fields || fields === s.fields ? s : { ...s, fields }
  })
}

/**
 * Move `fieldId` into whichever section `targetId` resolves to — before the
 * target field when the drop landed on one, else appended.
 *
 * This is the drop-time fallback. Field moves are normally applied live during
 * dragover by `applyFieldMove`, which only understands targets that are either a
 * field or a section's field-container group; a drop on a section HEADER
 * (`section:<id>`) is neither, so it lands here instead.
 */
export function placeFieldByTarget(
  secs: FormSection[],
  fieldId: string,
  targetId: string | null,
): FormSection[] {
  const destId = resolveSectionId(secs, targetId)
  if (!destId) return secs
  let moved: FormField | undefined
  const stripped = secs.map(s => {
    if (s.fields.some(f => f.id === fieldId)) {
      moved = s.fields.find(f => f.id === fieldId)
      return { ...s, fields: s.fields.filter(f => f.id !== fieldId) }
    }
    return s
  })
  if (!moved) return secs
  return stripped.map(s => {
    if (s.id !== destId) return s
    if (isFieldTarget(targetId) && s.fields.some(f => f.id === targetId)) {
      const idx = s.fields.findIndex(f => f.id === targetId)
      return { ...s, fields: [...s.fields.slice(0, idx), moved!, ...s.fields.slice(idx)] }
    }
    return { ...s, fields: [...s.fields, moved!] }
  })
}

/** True when both trees carry the same section ids holding the same field ids
 *  in the same order — i.e. no field actually moved. */
export function sameFieldLayout(a: FormSection[], b: FormSection[]): boolean {
  if (a.length !== b.length) return false
  return a.every((s, i) => {
    const other = b[i]
    return (
      s.id === other.id &&
      s.fields.length === other.fields.length &&
      s.fields.every((f, j) => f.id === other.fields[j].id)
    )
  })
}
// ─── Inline select options (z8nrz7djxb) ──────────────────────────────────────
//
// A `select` field's inline options are a FLAT, single-container list, which is
// what separates them from the canvas helpers above. The cross-container
// `removeChild` crash that forced `applyFieldMove` to own the move during
// dragover (86eyfxwky) cannot happen here — a same-container shuffle is one
// React reconciles with `insertBefore`. The move is still applied to state
// during dragover, for two reasons that do survive: application state stays the
// source of truth (so OptimisticSortingPlugin bails instead of touching the
// DOM), and a snapshot/restore cancel is then the same shape the canvas
// already uses.

/**
 * Sortable-group id for one select field's inline options list.
 *
 * Distinct per field and namespaced away from `sectionGroupId` so an option row
 * and a canvas field can never land in the same sortable group — the options
 * list renders INSIDE the canvas's DragDropProvider, so this prefix is what
 * keeps the two drag contexts from resolving into each other (AC1.6).
 */
export const optionGroupId = (fieldId: string) => `opts:${fieldId}`

/** Sortable id for one inline option row.
 *
 *  The prefix keeps option ids out of `resolveSectionId`'s namespace (it looks
 *  for `sec:`/`section:` and otherwise matches against real field ids, so an
 *  `opt:` id resolves to null). Note it does NOT make `isFieldTarget` reject
 *  one — that only screens the two section prefixes, so `isFieldTarget('opt:x')`
 *  is `true`. Harmless today because every caller pairs it with a
 *  `fields.some(f => f.id === targetId)` check, but don't treat it as a guard. */
export const optionSortableId = (optionId: string) => `opt:${optionId}`

/** dnd-kit `type`/`accept` value for option rows — see `isOptionRowTarget` for
 *  why this is a convenience, not the thing that provides isolation. */
export const OPTION_DRAG_TYPE = 'option'

/**
 * True when `targetId` is a row of THIS options list.
 *
 * This is the real guard for AC1.6, and it has to exist in our code rather than
 * being delegated to dnd-kit. A sortable `group` only drives index math; it
 * does not gate collision detection, and none of the builder's `useSortable`
 * sites constrain targets — so a canvas field or a section card genuinely can
 * win the collision for an option drag, and vice versa.
 *
 * That matters because `@dnd-kit/helpers`' `move` does something actively
 * harmful when the target is off-list: it cannot find the target index, falls
 * through to its `hasSortableIndices` branch, and applies
 * `arrayMove(items, source.initialIndex, source.index)`. `initialIndex` is the
 * PRE-drag index while the array has already been rewritten by earlier
 * dragovers, so it moves an option the user never touched. Screening the target
 * first is what makes an off-list target a no-op instead of a silent corruption.
 */
export function isOptionRowTarget(targetId: string | null, options: SelectOption[]): boolean {
  if (!targetId) return false
  return options.some(o => optionSortableId(o.id) === targetId)
}

/**
 * Move the option at `from` to `to`, preserving every other option's relative
 * order (AC1.2, AC2.2).
 *
 * The arrows' reorder — called with `i ± 1`. The drag path resolves its own
 * landing index through dnd-kit's `move` (see `applyOptionMove` for why, and
 * for how the two are held equivalent). Options are carried by reference, never
 * rebuilt, so a move cannot rewrite a label or mint a new id (AC3.2, AC3.3).
 *
 * Returns the input array unchanged — same reference, so React bails out of the
 * re-render and nothing is marked dirty — for any move that isn't one: equal
 * indices (AC4.3) or an index outside the list.
 */
export function moveOption(options: SelectOption[], from: number, to: number): SelectOption[] {
  if (from === to) return options
  if (from < 0 || to < 0 || from >= options.length || to >= options.length) return options
  const next = [...options]
  const [moved] = next.splice(from, 1)
  next.splice(to, 0, moved)
  return next
}

/**
 * Apply an in-flight option drag to one field's options list.
 *
 * Landing-index resolution is delegated to `@dnd-kit/helpers`' `move` rather
 * than recomputed from the pointer position, so the committed order is exactly
 * the one the drag preview showed (AC1.3) and stays correct if dnd-kit changes
 * its drop-position geometry.
 *
 * That leaves two functions rather than literally one (AC3.1): `move` owns the
 * drag's index math, `moveOption` owns the arrows'. What AC3.1 actually
 * requires is observable — the same end order must save identically — and since
 * a saved definition is `options.map(o => o.label)`, derived purely from array
 * order, that holds as long as both are correct. `formBuilderDnd.test.ts`
 * asserts the equivalence directly for every position in a list rather than
 * resting on the claim. Reimplementing dnd-kit's geometry just to route the
 * drag through `moveOption` would trade a tested property for an untested one.
 *
 * Returns the input array unchanged when the drag doesn't resolve to a move.
 *
 * `move` is handed the rows' SORTABLE ids, not the options themselves. The two
 * differ — a row registers as `optionSortableId(o.id)` so an option can never
 * be mistaken for a canvas field (AC1.6) — and `move` matches the event's
 * source/target against the ids of whatever array it is given. Passing
 * `options` directly therefore matches nothing and silently returns the list
 * unchanged, which is a no-op reorder that looks like a working one.
 */
export function applyOptionMove(options: SelectOption[], event: FieldDragEvent): SelectOption[] {
  const targetId = event?.operation?.target?.id
  // Screened BEFORE `move` — see isOptionRowTarget. An off-list target must not
  // reach `move`, whose fallback would reorder by a stale index.
  if (!isOptionRowTarget(targetId == null ? null : String(targetId), options)) return options
  const ids = options.map(o => optionSortableId(o.id))
  const nextIds = move(ids, event)
  if (nextIds === ids) return options
  const byId = new Map(ids.map((id, i) => [id, options[i]]))
  const next = nextIds.map((id: string) => byId.get(id)).filter(Boolean) as SelectOption[]
  // Bail rather than persist a partial list if an id failed to resolve.
  return next.length === options.length ? next : options
}

/** True when both lists hold the same option ids in the same order — i.e. no
 *  option actually moved. Compares ids, not labels, so a list carrying
 *  duplicate labels still reports honestly (AC4.4). */
export function sameOptionOrder(a: SelectOption[], b: SelectOption[]): boolean {
  if (a.length !== b.length) return false
  return a.every((o, i) => o.id === b[i].id)
}

/**
 * Apply an in-flight option drag to whichever field in the section tree owns
 * the list, leaving every other field and section untouched.
 *
 * Mirrors `applyFieldMove`'s reference discipline: sections and fields that
 * didn't change come back by reference, and a drag that resolves to no move
 * returns the input tree — so a no-op drop can't mark the form dirty (AC4.3).
 */
export function applyOptionMoveInSections(
  sections: FormSection[],
  fieldId: string,
  event: FieldDragEvent,
): FormSection[] {
  const sectionIdx = sections.findIndex(s => s.fields.some(f => f.id === fieldId))
  if (sectionIdx === -1) return sections
  const section = sections[sectionIdx]
  const fieldIdx = section.fields.findIndex(f => f.id === fieldId)
  const field = section.fields[fieldIdx]

  const options = applyOptionMove(field.options, event)
  if (options === field.options) return sections

  const fields = [...section.fields]
  fields[fieldIdx] = { ...field, options }
  const next = [...sections]
  next[sectionIdx] = { ...section, fields }
  return next
}

/** Restore one field's options list — the cancel path for an option drag
 *  (AC1.4, AC1.5), paired with a snapshot taken at dragstart. */
export function restoreFieldOptions(
  sections: FormSection[],
  fieldId: string,
  options: SelectOption[],
): FormSection[] {
  const sectionIdx = sections.findIndex(s => s.fields.some(f => f.id === fieldId))
  if (sectionIdx === -1) return sections
  const section = sections[sectionIdx]
  const fieldIdx = section.fields.findIndex(f => f.id === fieldId)
  const field = section.fields[fieldIdx]
  if (sameOptionOrder(field.options, options)) return sections

  const fields = [...section.fields]
  fields[fieldIdx] = { ...field, options }
  const next = [...sections]
  next[sectionIdx] = { ...section, fields }
  return next
}
